import { t } from '../i18n';
import { useEffect, useState } from 'react';
import { collection } from 'firebase/firestore';
import { db } from '../firebase';
import { firestoreCache, CACHE_KEYS, CACHE_TTL } from '../utils/firestoreCache';
import { guardedGetDocs } from '../utils/guardedFirestore';
import { ReviewTag } from '../types/review';

type UseReviewTagsResult = {
  tags: ReviewTag[];
  loading: boolean;
  error: string | null;
};

export const useReviewTags = (): UseReviewTagsResult => {
  const [tags, setTags] = useState<ReviewTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchTags = async () => {
      const cached = firestoreCache.get<ReviewTag[]>(CACHE_KEYS.REVIEW_TAGS);
      if (cached) {
        if (!cancelled) {
          setTags(cached);
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const snapshot = await guardedGetDocs(collection(db, 'reviewTags'));
        if (!snapshot) {
          if (!cancelled) {
            setTags([]);
            setLoading(false);
          }
          return;
        }

        const fetched = snapshot.docs
          .map((docSnap) => {
            const data = docSnap.data();
            return {
              id: docSnap.id,
              name: String(data.name || ''),
              category: (data.category || 'evaluation_style') as ReviewTag['category'],
              order: Number(data.order || 0),
            } as ReviewTag;
          })
          .filter((tag) => tag.name.length > 0)
          .sort((a, b) => a.order - b.order);

        // 重複するタグ名を排除する（最初の要素のみを残す）
        const uniqueFetched = fetched.filter((tag, index, self) =>
          index === self.findIndex((t) => t.name === tag.name)
        );

        firestoreCache.set(CACHE_KEYS.REVIEW_TAGS, uniqueFetched, CACHE_TTL.REVIEW_TAGS);

        if (!cancelled) {
          setTags(uniqueFetched);
        }
      } catch (e) {
        console.error('[useReviewTags] Failed to fetch review tags:', e);
        if (!cancelled) {
          setError(t("タグの読み込みに失敗しました"));
          setTags([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchTags();

    return () => {
      cancelled = true;
    };
  }, []);

  return { tags, loading, error };
};
